import { useRouter } from 'next/router';
import { FC, useEffect } from 'react';

import { Button, Group, Text } from '@mantine/core';

import {
    CompanyProfile,
    CompanySubscription,
    PermissionCompany,
    PublishedStatus,
    SubscriptionBillingCycle,
    SubscriptionType,
} from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { MarketingHelpers } from 'helpers/MarketingHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { Alert } from 'components/alert/Alert';
import { useProfilePermission } from 'components/company-profile/hooks/use-profile-permission';
import { useTeam } from 'hooks/use-team';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import { SubscriptionModalService } from 'services/SubscriptionModalService';

import { SubscriptionButton } from '../components/SubscriptionButton';

const CompanySubscriptionNotice: FC<{ company: CompanyProfile }> = ({ company }) => {
    const router = useRouter();
    const { team } = useTeam(company.team);
    const canEdit = useProfilePermission(PermissionCompany.EDIT, company);

    const { workspace: inWorkspace } = useWorkspaceConfig();

    const subscription = SubscriptionHelpers.getSubscriptionData(
        SubscriptionType.COMPANY,
        team?.subscriptions,
    ).subscription;

    const showUpgrade = Boolean(
        team &&
            canEdit &&
            !inWorkspace &&
            subscription !== CompanySubscription.PREMIUM &&
            !(company.status === PublishedStatus.DRAFT && subscription === CompanySubscription.NONE),
    );

    const openUpgradeModal = () => {
        if (!team) return;

        SubscriptionModalService.open({
            type: SubscriptionType.COMPANY,
            toSubscription: CompanySubscription.PREMIUM,
            team,
            redirectUrl: CompanyHelpers.urls.view(company.slug),
            defaultBillingCycle: SubscriptionBillingCycle.YEARLY,
        });
    };

    useEffect(() => {
        const { action, ...updatedQuery } = router.query;

        if (action === 'upgrade' && showUpgrade) {
            openUpgradeModal();

            router.replace({ pathname: router.pathname, query: updatedQuery }, undefined, { shallow: true }).then();
        }
    }, [router, showUpgrade]);

    if (!team || !showUpgrade) {
        return null;
    }

    return (
        <Alert
            title="Upgrade to Premium"
            rightSection={
                <Group gap="sm" justify="center" wrap="nowrap">
                    <SubscriptionButton
                        type={SubscriptionType.COMPANY}
                        toSubscription={CompanySubscription.PREMIUM}
                        team={team}
                        redirectUrl={CompanyHelpers.urls.view(company.slug)}
                        defaultBillingCycle={SubscriptionBillingCycle.YEARLY}
                        buttonLabel="Upgrade Profile"
                        buttonProps={{ variant: 'white', size: 'sm', fullWidth: false }}
                    />

                    <Button
                        component="a"
                        href={MarketingHelpers.urls.resourcepro.pricing()}
                        variant="outline"
                        color="white"
                        size="sm"
                    >
                        Why Upgrade?
                    </Button>
                </Group>
            }
            radius={0}
        >
            <Text inherit>
                Upgrade today to make your profile and products 24/7 discoverable in search results. In addition, go
                past your 5 free leads: see everyone who scanned or messaged you, what they were interested in, and chat
                with them directly.
            </Text>
        </Alert>
    );
};

export { CompanySubscriptionNotice };
